import { ClassicPreset as Classic, type GetSchemes, NodeEditor } from "rete";
import {
  type Area2D,
  AreaExtensions,
  AreaPlugin,
  Drag,
} from "rete-area-plugin";
import {
  ConnectionPlugin,
  Presets as ConnectionPresets,
} from "rete-connection-plugin";

import {
  type VueArea2D,
  VuePlugin,
  Presets as VuePresets,
} from "rete-vue-plugin";

import {
  Presets as ArrangePresets,
  AutoArrangePlugin,
} from "rete-auto-arrange-plugin";
import { DataflowEngine, type DataflowNode } from "rete-engine";

import {
  type ContextMenuExtra,
  ContextMenuPlugin,
  Presets as ContextMenuPresets,
} from "rete-context-menu-plugin";

import gpsImg from "@/assets/gps.svg";
import satImg from "@/assets/sat.svg";
import tempImg from "@/assets/temp.svg";
import LCDReadOnly, * as LCDReadOnlyModule from "@/components/LCDReadOnly.vue";
import ReadOnlyText from "@/components/ReadOnlyText.vue";
import mbImg from "../assets/motherboard.svg";
import plcImg from "../assets/plc.svg";

const LCDComponent = LCDReadOnlyModule.default || LCDReadOnlyModule;
// console.log("LCDComponent:", LCDComponent);
export type Node = MotherBoard | SAT | LORa | LTE | PLC;
type Conn = Connection<Node, Node>; // <-- Generico che comprende tutte le connessioni possibili
// | Connection<NumberNode, AddNode> // <-- Modo più sicuro e specifico
// | Connection<AddNode, AddNode>
// | Connection<AddNode, NumberNode>
// | Connection<SubtractNode, AddNode>
// | Connection<AddNode, SubtractNode>
// | Connection<SubtractNode, NumberNode>
// | Connection<MotherBoard, AddNode>;
export type Schemes = GetSchemes<Node, Conn>;

class Connection<A extends Node, B extends Node> extends Classic.Connection<
  A,
  B
> { }

export class LCDReadOnlyControl extends Classic.Control {
  // private _data = reactive({ text: "" });
  public text: string;

  constructor(text: string) {
    super();
    this.text = text;
    (this as any).props = { id: this.id };
  }

  setText(newText: string) {
    this.text = newText;
    (this as any).props = { id: this.id };
  }
}

class MotherBoard extends Classic.Node implements DataflowNode {
  width = 270;
  height = 270;

  constructor() {
    super("MotherBoard");
    this.addOutput("value", new Classic.Output(socket, "output"));
    this.addInput("value", new Classic.Input(socket, "input"));
    const lcd = new LCDReadOnlyControl("Caricamento dati...");
    // Passa l'id del CONTROLLO!
    (lcd as any).props = { id: lcd.id };
    // console.log("lcd.id:", lcd.id);
    this.addControl("lcd", lcd);
    // const control = this.controls["lcd"];
    // (control as any).props = { id: control?.id };
  }

  // @ts-ignore
  setValue(text) {
    //@ts-ignore
    this.props = { text: text, id: id };
  }

  data() {
    // puoi mettere qui altre info se usi il dataflow
    return {};
  }
}

class SAT extends Classic.Node implements DataflowNode {
  width = 210;
  height = 240;

  constructor() {
    super("SAT");
    this.addOutput("value", new Classic.Output(socket, "output"));
    this.addInput("value", new Classic.Input(socket, "input"));
    this.addControl("lcd", new LCDReadOnlyControl("Caricamento dati..."));
  }

  // @ts-ignore
  setValue(text) {
    //@ts-ignore
    this.props = { text: text };
  }

  data() {
    // puoi mettere qui altre info se usi il dataflow
    return {};
  }
}

class LORa extends Classic.Node implements DataflowNode {
  width = 230;
  height = 230;

  constructor() {
    super("LORa");
    this.addInput("value", new Classic.Input(socket, "input"));
    this.addOutput("value", new Classic.Output(socket, "output"));
    this.addControl("lcd", new LCDReadOnlyControl("Caricamento dati..."));
  }

  // @ts-ignore
  setValue(text) {
    //@ts-ignore
    this.props = { text: text };
  }

  data() {
    // puoi mettere qui altre info se usi il dataflow
    return {};
  }
}

class LTE extends Classic.Node implements DataflowNode {
  width = 290;
  height = 240;

  constructor() {
    super("LTE/5G");
    this.addInput("value", new Classic.Input(socket, "input"));
    this.addOutput("value", new Classic.Output(socket, "output"));
    this.addControl("lcd", new LCDReadOnlyControl("Caricamento dati..."));
  }

  // @ts-ignore
  setValue(text) {
    //@ts-ignore
    this.props = { text: text };
  }
  data() {
    // puoi mettere qui altre info se usi il dataflow
    return {};
  }
}

class PLC extends Classic.Node implements DataflowNode {
  width = 210;
  height = 240;

  constructor() {
    super("PLC");
    this.addOutput("value", new Classic.Output(socket, "output"));
    this.addInput("value", new Classic.Input(socket, "input"));
    this.addControl("lcd", new LCDReadOnlyControl("Caricamento dati..."));
  }

  // @ts-ignore
  setValue(text) {
    //@ts-ignore
    this.props = { text: text };
  }

  data() {
    // puoi mettere qui altre info se usi il dataflow
    return {};
  }
}

export type AreaExtra =
  | Area2D<Schemes>
  | VueArea2D<Schemes>
  | ContextMenuExtra
  | {
    type: "drag" | "dragged" | "translate" | "translated";
    [key: string]: any;
  };
const socket = new Classic.Socket("socket");

function makeNode(label: string) {
  if (label === "SAT") return new SAT();
  if (label === "MotherBoard") return new MotherBoard();
  if (label === "LORa") return new LORa();
  if (label === "LTE" || label === "LTE/5G") return new LTE();
  if (label === "PLC") return new PLC();
  return null;
}

export function normalizeLabel(lbl?: string) {
  if (!lbl) return undefined as any;
  const s = lbl.trim().toUpperCase();
  if (s === "LTE" || s === "LTE/5G" || s === "LTE5G") return "LTE/5G";
  if (s === "LORA") return "LORa";
  if (s === "MOTHERBOARD") return "MotherBoard";
  if (s === "SAT") return "SAT";
  if (s === "PLC") return "PLC";
  return lbl;
}

async function ensureNode(
  editor: NodeEditor<Schemes>,
  label: string
): Promise<Node> {
  let node = editor.getNodes().find((n) => n.label === label) as
    | Node
    | undefined;
  if (!node) {
    node = makeNode(label)!;
    await editor.addNode(node);
  }
  return node;
}

// export async function aggiungiNodoDaAI(
//   editor: NodeEditor<Schemes>,
//   aiResult: {
//     type: string;
//     actions: {
//       addNewNode?: boolean;
//       deleteNode?: string;
//       connectTo?: {
//         target: string;
//         sourceOutput: string;
//         targetInput: string;
//       };
//     };
//     context: string;
//   }
// ) {
//   const srcRaw = aiResult.type;
//   const tgtRaw = aiResult.actions?.connectTo?.target;
//   let src = normalizeLabel(srcRaw);
//   let tgt = normalizeLabel(tgtRaw);

//   // 🔒 Regola: se è coinvolta la MotherBoard, deve essere la SORGENTE.
//   if (tgt === "MotherBoard" && src) [src, tgt] = [tgt, src];

//   // se manca uno dei due, non facciamo nulla
//   if (!src || !tgt) return;

//   // crea/recupera i nodi necessari (non ci basiamo più solo su addNewNode)
//   const sourceNode = await ensureNode(editor, src);
//   const targetNode = await ensureNode(editor, tgt);

//   const sourceOutput = aiResult.actions?.connectTo?.sourceOutput || "value";
//   const targetInput = aiResult.actions?.connectTo?.targetInput || "value";

//   await editor.addConnection(
//     new Connection(sourceNode, sourceOutput, targetNode, targetInput)
//   );

//   // eventuale delete
//   if (aiResult.actions?.deleteNode) {
//     const toRemove = editor
//       .getNodes()
//       .find((n) => n.label === aiResult.actions!.deleteNode);
//     if (toRemove) await editor.removeNode(toRemove.id);
//   }
// }

export async function createEditor(
  container?: HTMLElement,
  skipDefaults = false
): Promise<{
  area: AreaPlugin<Schemes, AreaExtra>;
  editor: NodeEditor<Schemes>;
  layout: (scale?: number) => Promise<void>;
  // connection?: ConnectionPlugin<Schemes, AreaExtra>;
  destroy: () => void;
}> {
  const vueRender = new VuePlugin<Schemes, AreaExtra>();
  const editor = new NodeEditor<Schemes>();
  const area = new AreaPlugin<Schemes, AreaExtra>(
    container || document.createElement("div")
  );
  // const drag = new Drag();
  const connection = new ConnectionPlugin<Schemes, AreaExtra>();

  const contextMenu = new ContextMenuPlugin<Schemes>({
    items: ContextMenuPresets.classic.setup([
      ["MotherBoard", () => new MotherBoard()],
      ["SAT", () => new SAT()],
      ["LORa", () => new LORa()],
      ["LTE/5G", () => new LTE()],
      ["PLC", () => new PLC()],
    ]),
  });

  editor.use(area);

  area.use(vueRender);
  // area.use(new Drag() as any);
  // area.use(drag as any);
  // AreaExtensions.enableDrag(area, {
  //   filter: () => true, // Permetti il drag di tutti i nodi  3 rif. a drag obsoleti
  // });
  area.use(connection);
  area.use(contextMenu);

  connection.addPreset(ConnectionPresets.classic.setup());

  //* ***** 1. La piu vicina finora, in base al return se mandata
  //** **** lcdcomponent o lcdreadonly | con ...ldcreadonly o ...lcdcomponent
  //*** *** {props: ..ecc..}..
  //*** *** la custom vue viene renderizzata ma senza passare i props
  //* *** * quindi i dati non vengono visualizzati nel nodo ma il css
  // ****** della vue si.. incompleta attualmente!
  //* ***** 2. completata logica forzando il render tramite method nella classe
  //** **** e assegnazione a var globale assegnata nella vue custom (da perfezionare caricamento dati)

  vueRender.addPreset(
    VuePresets.classic.setup({
      customize: {
        control(data) {
          // LCDReadOnly custom control
          if (data.payload instanceof LCDReadOnlyControl) {
            // console.log("arrivo qui? LCDReadOnlyControl", data.payload);
            // console.log("data.payload.id:", data.payload.id);
            // console.log("data.payload.text:", data.payload.text);

            return LCDComponent;
            //* Alternativa che comunque non riesce a passare le props
            // return {
            // 	...LCDReadOnly,
            // 	props: () => ({
            // 		id: (data.payload as LCDReadOnlyControl).id,
            // 		text: (data.payload as LCDReadOnlyControl).text,
            // 	}),
            // };
            // console.log("props lcdcomponent:", LCDReadOnly);
            // console.log("props:", props);
            //* Alternativa non funzionante ma che dovrebbe essere quella standard
            // return {
            //   component: LCDReadOnly,
            //   // either pass a static object
            //   props: {
            //     id: data.payload.id,
            //     text: data.payload.text,
            //   },
            // };
          }
          // fallback: classic controls
          if (data.payload instanceof Classic.InputControl) {
            return VuePresets.classic.Control;
          }
          // fallback: handle unknown types safely
          // console.log("Unknown control type, returning undefined");
          return undefined;
        },
      },
    })
  );

  vueRender.addPreset(VuePresets.contextMenu.setup());

  const dataflow = new DataflowEngine<Schemes>();
  editor.use(dataflow);

  const imgMap: Record<string, string> = {
    MotherBoard: mbImg,
    SAT: satImg,
    LORa: gpsImg,
    "LTE/5G": tempImg,
    PLC: plcImg,
  };

  // intercetta il render di ciascun nodo e appiccica <img> in background
  editor.addPipe((ctx: any) => {
    if (ctx.type === "nodecreate" || ctx.type === "nodecreated") {
      // rete.js talvolta mette il nodo in ctx.node o in ctx.data.node
      const n = (ctx as any).data ?? (ctx as any).data?.node;
      // console.log(" NNN→ ctx.node:", n);
      if (n) {
        // console.log(" → node instance:", n);
        const { id, label } = n as { id: string; label: string };
        const cls =
          "node-" +
          n.label
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        const view = area.nodeViews.get(id);
        if (view) {
          // rimuovo vecchia icona
          view.element.querySelector("img.rete-node-icon")?.remove();

          // se ho un URL, creo e prependo l’immagine
          const url = imgMap[label];
          // console.log("rendernode", label, url);
          if (url) {
            const img = document.createElement("img");
            img.src = url;
            img.className = "rete-node-icon";
            Object.assign(img.style, {
              position: "absolute",
              top: "0",
              left: "0",
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              zIndex: "0",
            });
            view.element.prepend(img);
          }
          // la inserisco qui perché il nodo viene renderizzato nel div dentro il div principale
          // che ha come classe iniziale .node-<classe_ref> e dopo selezionato
          // .node-selected (è così di default)
          const inner = view.element.querySelector<HTMLElement>(".node");
          inner?.classList.add(cls);
        }
      }
    }
    return ctx;
  });

  if (!skipDefaults) {
    const motherBoard = new MotherBoard();
    const sat = new SAT();
    const lora = new LORa();
    const lte5g = new LTE();
    const plc = new PLC();

    await editor.addNode(motherBoard);
    await editor.addNode(sat);
    await editor.addNode(lora);
    await editor.addNode(lte5g);
    await editor.addNode(plc);
  }

  // Per un'altra logica non scartata del tutto..
  // motherBoard.getLcdControl().setAreaReference(area, motherBoard.id);

  const arrange = new AutoArrangePlugin<Schemes>();
  arrange.addPreset(ArrangePresets.classic.setup());
  area.use(arrange);

  // if (editor.getNodes()) {
  // 	// console.log("editor.getnodes()", editor.getNodes());
  // }
  async function layout(scale = 0.8) {
    await arrange.layout();
    await AreaExtensions.zoomAt(area, editor.getNodes(), { scale });
    AreaExtensions.simpleNodesOrder(area);
  }

  if (!skipDefaults) await layout();

  const selector = AreaExtensions.selector();
  const accumulating = AreaExtensions.accumulateOnCtrl();
  AreaExtensions.selectableNodes(area, selector, { accumulating });

  async function process() {
    dataflow.reset();
    for (const node of editor.getNodes()) {
      // if (node instanceof AddNode /*|| node instanceof SubtractNode*/) {
      //   const val = await dataflow.fetch(node.id);
      //   console.log(node.id, "produces", val);
      //   area.update("control", (node.controls["result"] as any).id);
      // }
    }
  }

  editor.addPipe((context) => {
    if (
      context.type === "connectioncreated" ||
      context.type === "connectionremoved"
    ) {
      process();
    }
    return context;
  });

  process();

  return {
    area,
    editor,
    layout,
    // connection,
    destroy: () => area.destroy(),
  };
}

export { Connection, LORa, LTE, MotherBoard, SAT, PLC };
